<template>
  <v-dialog
    max-width="800"
    :value="value"
    persistent
    :fullscreen="$vuetify.breakpoint.smAndDown"
    @keydown.esc="close(selectedItem)"
  >
    <v-card tile v-if="value">
      <v-app-bar dark flat color="primary">
        <v-btn icon dark @click="close(selectedItem)">
          <v-icon>close</v-icon>
        </v-btn>
        <span class="title">{{
          value.selectedItem
            ? resx.labels.updateContractorCompany
            : resx.labels.newContractorCompany
        }}</span>
      </v-app-bar>

      <form @submit.prevent="save()" novalidate>
        <v-card-text>
          <!-- Compacto a proposito: inputs densos, sin gutters grandes, y los
               dos contactos en zonas enmarcadas para que se lean como bloques. -->
          <v-container class="edit-contractor-company pa-2">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  dense
                  :items="companies"
                  :label="resx.labels.company"
                  item-text="name"
                  item-value="_id"
                  v-model="selectedItem.company"
                  name="company"
                  :error-messages="errors.collect('company')"
                  :data-vv-as="resx.labels.company"
                  v-validate.initial="'required'"
                  required
                  :disabled="(selectedItem._id && Boolean(selectedItem.company)) || companies.length === 0"
                  :no-data-text="resx.messages.noSelectorDataAvailable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  dense
                  :items="contractors"
                  :label="resx.labels.contractor"
                  item-text="name"
                  item-value="_id"
                  v-model="selectedItem.contractor"
                  name="contractor"
                  :error-messages="errors.collect('contractor')"
                  :data-vv-as="resx.labels.contractor"
                  v-validate.initial="'required'"
                  required
                  :disabled="(selectedItem._id && Boolean(selectedItem.contractor)) || contractors.length === 0"
                  :no-data-text="resx.messages.noSelectorDataAvailable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  dense
                  data-vv-name="type"
                  :data-vv-as="resx.labels.contractorType"
                  :error-messages="errors.collect('type')"
                  v-validate.initial="'required'"
                  :items="contractorTypes"
                  v-model="selectedItem.type"
                  :label="resx.labels.contractorType"
                  item-text="name"
                  item-value="_id"
                  cache-items
                  return-object
                  required
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <comments-component
                  v-model="selectedItem.comments"
                  mode="edit"
                  :label="resx.labels.comments"
                  @input="selectedItem.comments = $event"
                />
              </v-col>
              <!-- Para la planilla PDF del reporte de fin de mes. Sin servicio,
                   un subcontratista figura como "Sub de <principal>". -->
              <v-col cols="12" md="6">
                <v-text-field
                  dense
                  v-model="selectedItem.service"
                  label="Servicio prestado"
                  name="service"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  dense
                  class="parent-contractor-select"
                  :items="parentCandidates"
                  label="Subcontratista de"
                  item-text="name"
                  item-value="_id"
                  v-model="selectedItem.parentContractorCompany"
                  clearable
                  :disabled="!selectedItem.company"
                  no-data-text="No hay otras contratistas en esta empresa"
                />
              </v-col>
              <v-col cols="12" class="mb-3">
                <v-textarea
                  dense
                  rows="1"
                  auto-grow
                  v-model="selectedItem.observations"
                  label="Observaciones"
                  hint="Aparecerán al revisar las obligaciones del contratista"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  dense
                  v-model="selectedItem.phone"
                  :label="resx.labels.phone"
                  name="phone"
                  :data-vv-as="resx.labels.phone"
                  :error-messages="errors.collect('phone')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  dense
                  v-model="selectedItem.email"
                  :label="resx.labels.email"
                  name="email"
                  v-validate.initial="'email'"
                  :data-vv-as="resx.labels.email"
                  :error-messages="errors.collect('email')"
                />
              </v-col>
            </v-row>

            <!-- Contactos: una tarjeta por contacto, numeradas por su posicion
                 en la lista. Quitar una renumera al instante; nada se guarda
                 hasta OK. Los campos contacto 1 y 2 de siempre los deriva el
                 backend de esta lista. -->
            <div class="d-flex align-center mt-2 mb-1">
              <span class="subtitle-2">Contactos</span>
              <v-spacer />
              <v-btn small text color="primary" @click="addContact">
                <v-icon small left>add</v-icon>Agregar contacto
              </v-btn>
            </div>
            <v-row dense>
              <v-col
                v-for="(contact, index) in selectedItem.contacts"
                :key="index"
                cols="12"
                md="6"
              >
                <v-sheet outlined rounded class="contact-zone pa-3">
                  <div class="d-flex align-center mb-1">
                    <span class="subtitle-2">Contacto {{ index + 1 }}</span>
                    <v-spacer />
                    <v-btn icon x-small :title="`Quitar contacto ${index + 1}`" @click="removeContact(index)">
                      <v-icon small color="error">close</v-icon>
                    </v-btn>
                  </div>
                  <v-text-field
                    dense
                    v-model="contact.name"
                    label="Nombre"
                    :name="`contact-name-${index}`"
                  />
                  <v-text-field
                    dense
                    v-model="contact.phone"
                    label="Teléfono"
                    :name="`contact-phone-${index}`"
                  />
                  <v-text-field
                    dense
                    v-model="contact.email"
                    label="Correo"
                    type="email"
                    :name="`contact-email-${index}`"
                    v-validate="'email'"
                    :data-vv-as="`correo del contacto ${index + 1}`"
                    :error-messages="errors.collect(`contact-email-${index}`)"
                  />
                  <!-- Como recibe los Envios este contacto, y si su nombre
                       entra en $CONTACTO. Solo vale para los Envios nuevos:
                       el envio viejo sigue usando contacto 1 y 2. -->
                  <div class="contact-delivery d-flex flex-wrap align-center">
                    <v-select
                      dense
                      outlined
                      hide-details
                      class="contact-email-mode"
                      v-model="contact.emailMode"
                      :items="emailModeOptions"
                      item-text="text"
                      item-value="value"
                      label="Envíos"
                      :prepend-inner-icon="emailModeIcon(contact.emailMode)"
                      :menu-props="{ offsetY: true }"
                      :name="`contact-email-mode-${index}`"
                    >
                      <template v-slot:item="{ item }">
                        <v-icon small class="mr-2" color="primary">{{ item.icon }}</v-icon>
                        <span>{{ item.text }}</span>
                      </template>
                    </v-select>
                    <v-checkbox
                      dense
                      hide-details
                      color="primary"
                      class="contact-include-name ml-3"
                      v-model="contact.includeName"
                      label="Incluir nombre"
                      title="Suma el nombre de este contacto a $CONTACTO en los Envíos"
                      :name="`contact-include-name-${index}`"
                    />
                    <v-checkbox
                      dense
                      hide-details
                      color="primary"
                      class="contact-include-name ml-3"
                      v-model="contact.reportRepresentative"
                      label="Representante en reporte de fin de mes"
                      title="Sale en la columna Contacto del reporte de fin de mes. Pueden ser varios."
                      :name="`contact-report-representative-${index}`"
                    />
                  </div>
                </v-sheet>
              </v-col>
              <v-col v-if="!selectedItem.contacts || !selectedItem.contacts.length" cols="12">
                <div class="body-2 grey--text">Sin contactos cargados.</div>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" md="6">
                <v-switch
                  dense
                  hide-details
                  color="primary"
                  :label="
                    `${resx.labels.contractorsAssoc} ${
                      selectedItem.isActive
                        ? resx.labels.active
                        : resx.labels.inactive
                    }`
                  "
                  v-model="selectedItem.isActive"
                >
                </v-switch>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch
                  dense
                  hide-details
                  color="primary"
                  :label="
                    `${resx.labels.sendEmailActivation} ${
                      selectedItem.sendEmail
                        ? resx.labels.active
                        : resx.labels.inactive
                    }`
                  "
                  v-model="selectedItem.sendEmail"
                >
                </v-switch>
              </v-col>
            </v-row>

            <!-- EPP: el interruptor general y, debajo, los elementos en una
                 grilla de tres columnas, densos y sin detalles. -->
            <v-sheet outlined rounded class="contact-zone pa-3 mt-3">
              <v-switch
                dense
                hide-details
                color="primary"
                class="mt-0"
                :label="`Controlar EPP`"
                v-model="selectedItem.personalProtectionItems.verify"
              >
              </v-switch>
              <!-- Con "Controlar EPP" apagado la grilla se pliega y queda
                   solo el interruptor: los elementos no aplican. -->
              <v-expand-transition>
              <v-row v-show="selectedItem.personalProtectionItems.verify" dense class="mt-1">
                <v-col
                  v-for="item in protectionItems"
                  :key="item.key"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-switch
                    dense
                    hide-details
                    color="primary"
                    class="mt-0"
                    :disabled="!selectedItem.personalProtectionItems.verify"
                    :label="item.label"
                    v-model="selectedItem.personalProtectionItems[item.key]"
                  >
                  </v-switch>
                </v-col>
              </v-row>
              </v-expand-transition>
            </v-sheet>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="close(selectedItem)">{{ resx.labels.cancel }}</v-btn>
          <v-btn class="primary" type="submit" :disabled="!isValidForm">{{
            resx.labels.ok
          }}</v-btn>
        </v-card-actions>
      </form>
    </v-card>
    <v-dialog
      max-width="600"
      :value="errorMessage"
      persistent
      :fullscreen="$vuetify.breakpoint.smAndDown"
      @keydown.esc="errorMessage=''"
    >
      <v-card>
        <v-card-text>
          <v-container grid-list-xl>
            <v-row wrap>
              {{ errorMessage }}
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="errorMessage=''">{{
            resx.labels.ok
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
"use strict";

import companyService from "@/services/CompanyService";
import { contactsFrom, emptyContact, EMAIL_MODE_OPTIONS } from "@/utils/contractor-contacts";
import contractorCompanyService from "@/services/ContractorCompanyService";
import contractorService from "@/services/ContractorService";
import contractorTypeService from "@/services/ContractorTypeService";
import { sprintf } from "sprintf-js";
import commentsComponent from "@/components/common/comments.vue";
import constants from "@/utils/constants";

export default {
  name: "edit-contractor-company",
  async mounted() {
    this.companies = await companyService.getCompanies();
    this.contractors = await contractorService.list();
    this.contractorTypes = await contractorTypeService.list();
  },
  components: {
    commentsComponent
  },
  data() {
    return {
      selectedItem: {
        personalProtectionItems: constants.PERSONAL_PROTECTION_ITEMS_PLACEHOLDER
      },
      originalSelectedItem: null,
      companies: [],
      contractors: [],
      contractorTypes: [],
      errorMessage: "",
      emailModeOptions: EMAIL_MODE_OPTIONS,
      // Los elementos de proteccion personal, en el orden en que se muestran.
      // Las claves son las de personalProtectionItems del modelo.
      protectionItems: [
        { key: "helmet", label: "Casco" },
        { key: "securityShoes", label: "Botas" },
        { key: "gloves", label: "Guantes" },
        { key: "goggles", label: "Gafas" },
        { key: "auditoryProtector", label: "Protector auditivo" },
        { key: "gasMask", label: "Máscara de gas" },
        { key: "uniform", label: "Uniforme" },
        { key: "reflectiveVest", label: "Chaleco reflectante" },
        { key: "safetyHarness", label: "Arnés de seguridad" },
        { key: "weldingMask", label: "Máscara para soldar" }
      ]
    };
  },
  methods: {
    // La tarjeta nueva nace con el modo y la bandera que le tocan por su
    // posicion (la primera al "para" y con nombre, la segunda en copia, el
    // resto sin correo), para que una contratista recien cargada mande igual
    // que antes sin tocar nada.
    addContact() {
      if (!Array.isArray(this.selectedItem.contacts)) {
        this.$set(this.selectedItem, "contacts", []);
      }
      this.selectedItem.contacts.push(emptyContact(this.selectedItem.contacts.length));
    },
    emailModeIcon(mode) {
      const option = this.emailModeOptions.find(o => o.value === mode);
      return option ? option.icon : "mail_outline";
    },
    // Quitar el 2 hace que el 3 pase a verse como 2: la numeracion es la
    // posicion en la lista. El backend registra lo quitado recien al guardar.
    removeContact(index) {
      this.selectedItem.contacts.splice(index, 1);
    },
    async save() {
      const resx = this.$store.state.locale.resx;
      let result = null;  
      let payload = Object.assign({}, this.selectedItem);
      if (!this.selectedItem._id) {
        result = await contractorCompanyService.create(payload);
        if (result.error) {
          const status = result.error.response.status;
          if (status == 400) {
            this.errorMessage = resx.messages.uniqueContractorCompanyAndType;
          } else if (status == 404 || status == 500) {
            this.errorMessage = resx.messages.contractorCompanyCreationError;
          }
        } else {
          this.$emit("save", { isNew: true, item: result.data });
        }
      } else {
        result = await contractorCompanyService.edit(payload);
        this.$emit("save", { isNew: false, item: result });
      }
    },
    async close(newContractorCompany) {
      if (JSON.stringify(this.originalSelectedItem) != JSON.stringify(this.selectedItem)) {
        await this.botConfirmDialog({
          title: `${
          this.resx.labels.edit
          } ${this.resx.labels.newContractorCompany.toLowerCase()}`,
          content: sprintf(
          this.resx.messages.noSavedChanges,
          newContractorCompany.name
          )
        });
        this.$emit("close");
      } else {
        this.$emit("close");
      }
    },
  },
  props: {
    value: {},
    // Las contratistas ya cargadas en el listado: de ahi sale el selector de
    // "Subcontratista de", sin pedirle nada mas al backend.
    contractorCompanies: { type: Array, default: () => [] }
  },
  computed: {
    // Otras contratistas vivas de la misma empresa que no son a su vez
    // subcontratistas: el backend solo acepta un nivel.
    parentCandidates() {
      const companyId = this.selectedItem.company;
      if (!companyId) {
        return [];
      }
      const idOf = value => (value && value._id) || value;
      return this.contractorCompanies
        .filter(cc => !cc.isDeleted
          && idOf(cc.company) === companyId
          && cc._id !== this.selectedItem._id
          && !cc.parentContractorCompany)
        .map(cc => ({ _id: cc._id, name: (cc.contractor && cc.contractor.name) || "" }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  watch: {
    value(val) {
      if (val && val.selectedItem) {
        this.selectedItem = JSON.parse(JSON.stringify(val.selectedItem));
        this.selectedItem.company = this.selectedItem.company._id;
        this.selectedItem.contractor = this.selectedItem.contractor._id;
        // La lista editable: la guardada, o contacto 1 y 2 de los campos
        // sueltos si la contratista es anterior a la lista.
        this.selectedItem.contacts = contactsFrom(this.selectedItem);
      } else {
        this.selectedItem = { isActive: true, personalProtectionItems: {}, contacts: [] };
      }
      // Copia profunda: con una superficial, la lista de contactos y el EPP
      // compartian referencia y editarlos no contaba como cambio sin guardar.
      this.originalSelectedItem = JSON.parse(JSON.stringify(this.selectedItem));
    },
    "selectedItem.contractor"(val) {
      if (val && !this.selectedItem._id) {
        const contractor = this.contractors.find(c => c._id == val);
        this.selectedItem.contactEmail = contractor.contactEmail;
        this.selectedItem.contactPhone = contractor.contactPhone;
        this.selectedItem.contactEmail1 = contractor.contactEmail1;
        this.selectedItem.contactPhone1 = contractor.contactPhone1;
      }
    }
  }
};
</script>

<style lang="scss">
.edit-contractor-company {
  // Zonas enmarcadas de los contactos y del EPP: borde suave y fondo apenas
  // distinto para que se lean como bloques sin agregar alto.
  .contact-zone {
    border-color: rgba(0, 0, 0, 0.12);
    background-color: rgba(0, 0, 0, 0.02);
  }
  // Los inputs densos de Vuetify igual dejan margen para el mensaje de error;
  // se achica un poco para que el modal entre sin scroll en una pantalla comun.
  .v-text-field.v-input--dense {
    margin-top: 0;
    padding-top: 0;
  }
  .v-input--switch.v-input--dense {
    margin-top: 4px;
  }
  // El modo de envio y "Incluir nombre" van en una misma linea al pie de la
  // tarjeta, separados del resto por un filete suave.
  .contact-delivery {
    margin-top: 6px;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  .contact-email-mode {
    flex: 1 1 auto;
    .v-input__slot {
      min-height: 36px !important;
      background-color: #fff;
    }
    .v-select__selection {
      font-size: 13px;
    }
    .v-input__prepend-inner {
      margin-top: 6px !important;
    }
  }
  .parent-contractor-select:not(.v-input--is-disabled) .v-input__slot {
    cursor: pointer;
  }
  .contact-include-name {
    flex: 0 0 auto;
    margin-top: 0;
    padding-top: 0;
    .v-label {
      font-size: 13px;
    }
  }
}
</style>
